import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { dashboardService } from '../../services/dashboard';
import FeatureTag from './components/FeatureTag';
import Toggle from './components/Toggle';

function ProjectListItem({ project, isActive, isSelected, onSelect }) {
  const tags = [
    { feature: 'routing', label: 'Routing', show: project.routing_enabled !== false },
    { feature: 'cache', label: 'Cache', show: project.cache_enabled },
    { feature: 'evals', label: 'Evals', show: project.eval_enabled },
  ].filter((t) => t.show);

  return (
    <button
      type="button"
      onClick={() => onSelect(project.hash)}
      className={`group text-left w-full p-3 rounded-xl border transition-all duration-150 ${
        isSelected
          ? 'border-theme-primary bg-theme-primary/10 shadow-sm'
          : 'border-transparent hover:bg-theme-secondary/40'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="font-medium truncate text-sm">{project.name}</span>
        {isActive && (
          <span className="shrink-0 w-2 h-2 rounded-full bg-theme-primary" title="Active project" />
        )}
      </div>
      {project.is_default && (
        <span className="text-[10px] uppercase tracking-wider opacity-40 font-medium">Default</span>
      )}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {tags.map((t) => (
            <FeatureTag key={t.feature} feature={t.feature} label={t.label} />
          ))}
        </div>
      )}
    </button>
  );
}

const CACHE_TTL_OPTIONS = [
  { label: '1 minute', seconds: 60 },
  { label: '10 minutes', seconds: 600 },
  { label: '1 hour', seconds: 3600 },
  { label: '6 hours', seconds: 21600 },
  { label: '1 day', seconds: 86400 },
];

function SettingRow({ title, description, checked, onChange, disabled, children }) {
  return (
    <div className="rounded-2xl border border-theme-secondary bg-theme-main overflow-hidden">
      <div className="flex items-center justify-between gap-4 p-4 sm:p-5">
        <div className="min-w-0">
          <div className="font-medium">{title}</div>
          {description && <p className="text-sm opacity-60 mt-0.5">{description}</p>}
        </div>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} label={title} />
      </div>
      {checked && children && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0 border-t border-theme-secondary/50">
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}

export default function ProjectsTab() {
  const navigate = useNavigate();
  const { projects, selectedHash, setSelectedProject, reloadProjects } = useProject();
  const [editingHash, setEditingHash] = useState(selectedHash);
  const [projectName, setProjectName] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    if (selectedHash) setEditingHash(selectedHash);
  }, [selectedHash]);

  const editingProject = projects.find((p) => p.hash === editingHash) || projects[0] || null;

  useEffect(() => {
    if (editingProject) setProjectName(editingProject.name);
    setShowDelete(false);
    setDeleteConfirm('');
  }, [editingProject?.hash]);

  const selectProject = (hash) => {
    setEditingHash(hash);
    setSelectedProject(hash);
  };

  const patchProject = (patch) => {
    if (!editingProject) return;
    dashboardService.patchProject(editingProject.hash, patch, reloadProjects, console.error);
  };

  const saveName = () => {
    const trimmed = projectName.trim();
    if (!editingProject || !trimmed || trimmed === editingProject.name) return;
    patchProject({ name: trimmed });
  };

  const confirmDelete = () => {
    if (!editingProject || deleteConfirm !== editingProject.name) return;
    dashboardService.deleteProject(
      editingProject.hash,
      () => {
        reloadProjects();
        const remaining = projects.filter((p) => p.hash !== editingProject.hash);
        if (remaining.length) setSelectedProject(remaining[0].hash);
        setShowDelete(false);
      },
      console.error,
    );
  };

  const evalSamplingPct = editingProject
    ? Math.round(Number(editingProject.eval_sampling_rate || 0.1) * 100)
    : 10;
  const cacheTtlSeconds = editingProject?.cache_ttl_seconds ?? 3600;
  const cacheTtlValue = CACHE_TTL_OPTIONS.some((o) => o.seconds === cacheTtlSeconds)
    ? cacheTtlSeconds
    : 3600;
  const routingEnabled = editingProject?.routing_enabled !== false;

  return (
    <div className="flex flex-col lg:flex-row gap-6 min-h-[480px]">
      {/* Left: project list */}
      <aside className="lg:w-1/4 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium opacity-60 uppercase tracking-wider">Projects</h2>
          <button
            type="button"
            onClick={() => navigate('/settings/projects/new/')}
            className="text-xs px-2.5 py-1 rounded-lg bg-theme-primary text-theme-button font-medium"
          >
            Add
          </button>
        </div>
        <div className="rounded-2xl border border-theme-secondary bg-theme-main p-2 space-y-1">
          {projects.length > 0 ? (
            projects.map((p) => (
              <ProjectListItem
                key={p.hash}
                project={p}
                isActive={p.hash === selectedHash}
                isSelected={p.hash === editingHash}
                onSelect={selectProject}
              />
            ))
          ) : (
            <p className="text-sm opacity-50 text-center py-8">No projects yet.</p>
          )}
        </div>
      </aside>

      {/* Right: project config */}
      <div className="lg:w-3/4 min-w-0">
        {editingProject ? (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium opacity-60 mb-2">Project name</label>
              <div className="flex gap-2 max-w-md">
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => e.key === 'Enter' && saveName()}
                  className="flex-1 rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm"
                />
                {projectName.trim() !== editingProject.name && (
                  <button
                    type="button"
                    onClick={saveName}
                    className="px-3 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium"
                  >
                    Save
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <SettingRow
                title="Routing"
                description="Route traffic to open-source models. When off, all traffic passes through to your provider."
                checked={editingProject.routing_enabled !== false}
                onChange={(on) => patchProject({ routing_enabled: on })}
              >
                <Link
                  to="/routing/"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-theme-secondary text-sm hover:border-theme-primary/50 hover:text-theme-primary transition-colors"
                >
                  Configure routing rules
                  <span aria-hidden>→</span>
                </Link>
              </SettingRow>

              <SettingRow
                title="Cache"
                description="Store routed request/response payloads for faster repeats."
                checked={!!editingProject.cache_enabled}
                onChange={(on) => patchProject({ cache_enabled: on })}
              >
                <label className="block text-sm">
                  Cache duration
                  <select
                    value={cacheTtlValue}
                    onChange={(e) => patchProject({ cache_ttl_seconds: Number(e.target.value) })}
                    className="mt-2 w-full max-w-xs rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm"
                  >
                    {CACHE_TTL_OPTIONS.map((opt) => (
                      <option key={opt.seconds} value={opt.seconds}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              </SettingRow>

              <SettingRow
                title="Shadow evals"
                description="Sample production traffic and score model quality in the background."
                checked={!!editingProject.eval_enabled}
                onChange={(on) => patchProject({ eval_enabled: on })}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm mb-2">Sampling rate (%)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      key={`${editingProject.hash}-${evalSamplingPct}`}
                      defaultValue={evalSamplingPct}
                      onBlur={(e) => {
                        const pct = Math.max(1, Math.min(100, Number(e.target.value) || 10));
                        const rate = pct / 100;
                        if (rate !== Number(editingProject.eval_sampling_rate)) {
                          patchProject({ eval_sampling_rate: rate });
                        }
                      }}
                      className="w-full max-w-xs rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="space-y-2.5 text-sm">
                    <label className="flex items-center gap-2.5 opacity-50 cursor-not-allowed">
                      <input type="checkbox" checked disabled className="rounded" />
                      Evaluate final model
                    </label>
                    <label className={`flex items-center gap-2.5 ${routingEnabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="checkbox"
                        checked={!!editingProject.eval_original_model}
                        disabled={!routingEnabled}
                        onChange={(e) => patchProject({ eval_original_model: e.target.checked })}
                        className="rounded accent-theme-primary"
                      />
                      Evaluate original model
                    </label>
                    <label className={`flex items-center gap-2.5 ${routingEnabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="checkbox"
                        checked={!!editingProject.eval_higher_models}
                        disabled={!routingEnabled}
                        onChange={(e) => patchProject({ eval_higher_models: e.target.checked })}
                        className="rounded accent-theme-primary"
                      />
                      Evaluate higher-tier models
                    </label>
                    <label className={`flex items-center gap-2.5 ${routingEnabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="checkbox"
                        checked={!!editingProject.eval_lower_models}
                        disabled={!routingEnabled}
                        onChange={(e) => patchProject({ eval_lower_models: e.target.checked })}
                        className="rounded accent-theme-primary"
                      />
                      Evaluate lower-tier models
                    </label>
                  </div>
                </div>
              </SettingRow>
            </div>

            {!editingProject.is_default && (
              <section className="pt-4 border-t border-theme-secondary">
                <h3 className="text-sm font-medium text-red-400 mb-2">Danger zone</h3>
                {!showDelete ? (
                  <button
                    type="button"
                    onClick={() => setShowDelete(true)}
                    className="px-4 py-2 rounded-lg border border-red-400/40 text-red-400 text-sm hover:bg-red-400/10 transition-colors"
                  >
                    Delete project
                  </button>
                ) : (
                  <div className="rounded-2xl border border-red-400/30 bg-red-400/5 p-5 max-w-lg">
                    <p className="text-sm mb-4">
                      This will permanently delete <strong>{editingProject.name}</strong> and all of its API keys.
                      This cannot be undone.
                    </p>
                    <label className="block text-sm mb-1 opacity-70">
                      Type <span className="font-mono font-medium">{editingProject.name}</span> to confirm
                    </label>
                    <input
                      type="text"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder={editingProject.name}
                      className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm mb-4"
                    />
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => { setShowDelete(false); setDeleteConfirm(''); }}
                        className="px-4 py-2 rounded-lg border border-theme-secondary text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={confirmDelete}
                        disabled={deleteConfirm !== editingProject.name}
                        className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium disabled:opacity-40"
                      >
                        Delete project
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-theme-secondary p-12 text-center opacity-60">
            Select a project to configure settings.
          </div>
        )}
      </div>

      <Outlet />
    </div>
  );
}
